import React, { useEffect } from 'react';
import NavBar from '../../components/NavBar';
import ResponsiveFooter from '../../components/ResponsiveFooter';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

// Apple-inspired color palette (matching LandingPage.jsx)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray600: '#6e6e6e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles (matching LandingPage.jsx)
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#000000',
  },
  subheading: {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    color: '#333333',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
    color: '#333333',
  },
  medium: {
    fontWeight: 500,
  }
};

// Animation variants
const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6,
      ease: [0.6, -0.05, 0.01, 0.99]
    } 
  }
};

const CompliancePage = () => {

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col"
      style={{
        color: colors.gray900,
        fontFamily: fontStyles.fontFamily
      }}>
      {/* Navigation */}
      <NavBar 
        activePage="compliance"
      />
      
      {/* Content */}
      <motion.div 
        className="relative py-20 overflow-hidden"
        initial="hidden"
        animate="visible"
        variants={fadeInVariants}
        style={{
          fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
          background: 'linear-gradient(to bottom, white, #f8faff)'
        }}
      >
        {/* Vertical Rails - Aligned with Footer positioning */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <div className="max-w-7xl mx-auto h-full relative" style={{ paddingLeft: 24, paddingRight: 24 }}>
            {/* Left vertical lines - slightly darker */}
            <div className="absolute left-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute left-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
            {/* Right vertical lines - slightly darker */}
            <div className="absolute right-4 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.18)',
                opacity: 0.8
              }} 
            />
            <div className="absolute right-8 top-0 bottom-0 w-px" 
              style={{
                background: 'rgba(30, 64, 175, 0.14)',
                opacity: 0.9
              }} 
            />
          </div>
        </div>
        
        <div className="container mx-auto px-6 pt-12 pb-0 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link to="/" className="inline-flex items-center text-sm transition-colors mb-10" 
            style={{color: colors.gray600}}
            onMouseOver={(e) => {e.currentTarget.style.color = colors.accent}}
            onMouseOut={(e) => {e.currentTarget.style.color = colors.gray600}}
          >
            <ArrowLeft size={14} className="mr-1" />
            Back to Home
          </Link>
          
          <div className="prose prose-lg max-w-none">
            <div className="flex flex-col items-start mb-6">
              <img src="/6.png" alt="SignPact Logo" className="h-12 mb-4" />
              <h1 className="text-4xl font-medium tracking-normal" 
                style={{
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontWeight: 500,
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  marginTop: '0.25rem'
                }}>
                Compliance.
              </h1>
            </div>
            
            <p className="text-sm mb-14" style={{color: colors.gray500}}>
              Last Updated: June 12, 2025
            </p>
            
            <p className="mb-6" style={fontStyles.body}>
              At SignPact, we understand that compliance is crucial for our customers. Our platform is designed to help you meet your regulatory obligations while providing the security and auditability required for business-critical documents. This page outlines our compliance measures and how we can help your organization maintain its compliance requirements.
            </p>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              1. E-Signature Compliance
            </h2>
            
            <p className="mb-6" style={fontStyles.body}>
              SignPact's electronic signature capabilities are designed to comply with major e-signature laws globally, enabling you to execute legally binding agreements electronically:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>ESIGN Act (United States):</strong> Our platform complies with the Electronic Signatures in Global and National Commerce Act, which ensures the legal validity of contracts and signatures in electronic form for interstate and foreign commerce.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>UETA (United States):</strong> SignPact adheres to the Uniform Electronic Transactions Act, which has been adopted by 47 states, the District of Columbia, Puerto Rico, and the U.S. Virgin Islands to provide uniform rules for electronic records and signatures.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>eIDAS Regulation (European Union):</strong> Our platform is designed with the requirements of the EU regulation on electronic identification and trust services for electronic transactions in mind. We support standard electronic signatures (SES) and are working toward advanced electronic signature (AES) capabilities.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>International Compliance:</strong> SignPact follows principles compatible with electronic signature laws in many other countries. For specific jurisdictional questions, we recommend consulting with legal counsel familiar with local requirements.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              2. Data Protection Compliance
            </h2>
            
            <p className="mb-6" style={fontStyles.body}>
              We take data protection seriously and have implemented measures to comply with relevant data protection regulations:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>GDPR (European Union):</strong> SignPact is designed to help you meet GDPR requirements when processing personal data. We act as a data processor for the information you upload, and our Privacy Policy outlines our data processing activities, data subject rights, and our security measures.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>CCPA/CPRA (California):</strong> We comply with the California Consumer Privacy Act as amended by the California Privacy Rights Act, respecting California residents' rights regarding their personal information.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Data Processing Agreements (DPAs):</strong> For enterprise customers, we offer data processing agreements that outline our data protection commitments, including security measures, data breach notification procedures, and data subject rights management.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Cross-border Data Transfers:</strong> SignPact implements appropriate safeguards for international data transfers, including standard contractual clauses when required.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              3. Security Certifications & Standards
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              As a Delaware C Corporation, we are actively working toward industry-standard security certifications:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>SOC 2 Compliance:</strong> We are on the path to SOC 2 compliance, implementing controls relevant to security, availability, processing integrity, confidentiality, and privacy. We expect to complete a SOC 2 Type I assessment in 2024, followed by Type II.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>ISO 27001:</strong> Our information security management system is being developed in line with ISO 27001 standards, with formal certification planned as we scale.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Cloud Security Alliance (CSA):</strong> SignPact follows CSA best practices for secure cloud operations and will complete the CSA STAR self-assessment in the coming months.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              4. Security & Data Protection Measures
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              Our platform incorporates multiple layers of security to protect your sensitive contract data:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Encryption:</strong> All data in transit is protected using TLS 1.2+. Data at rest is encrypted using AES-256 encryption. Document content, metadata, and user information are all encrypted in our database.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Access Controls:</strong> We implement role-based access controls (RBAC), multi-factor authentication, and least privilege principles to restrict access to customer data.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Secure Development:</strong> Our development practices include code reviews, regular security testing, and vulnerability management to ensure a secure codebase.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Monitoring and Logging:</strong> We maintain comprehensive logs of system activities and monitor for suspicious behavior, with alerts for potential security incidents.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Backup and Disaster Recovery:</strong> Regular backups, redundancy, and disaster recovery procedures ensure your data is protected against loss.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              5. Industry-Specific Compliance Support
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              SignPact provides features to help customers in regulated industries meet their compliance obligations:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Healthcare:</strong> While not HIPAA-certified yet, our security measures align with many HIPAA requirements. We are working toward formal HIPAA compliance and can execute Business Associate Agreements (BAAs) for enterprises.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Financial Services:</strong> Our platform includes features that support compliance with regulations such as SOX (Sarbanes-Oxley) for public companies, including audit trails and access controls.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Legal Industry:</strong> SignPact helps legal professionals meet ethical obligations and client confidentiality requirements with secure document handling and defined access permissions.
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              6. Compliance Features for Your Business
            </h2>
            
            <p className="mb-4" style={fontStyles.body}>
              SignPact offers several features to help your organization maintain compliance:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Comprehensive Audit Trails:</strong> Every action in the system—from document uploads to signature events—is recorded with user information, timestamps, and IP addresses, providing a complete audit history.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Electronic Signature Verification:</strong> Our platform captures and stores evidence of signing intent, including signer verification methods and timestamped actions.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Document Retention:</strong> Flexible retention policies allow you to set appropriate document lifespans in accordance with your regulatory requirements.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Customizable Workflows:</strong> Create approval processes that enforce compliance with your internal policies and external regulations.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>AI-Powered Compliance Scanning:</strong> Our AI can help identify potential compliance issues in your contracts, such as missing clauses or terms that might not align with relevant regulations (for informational purposes only).
              </li>
            </ul>
            
            <h2 className="text-2xl mb-4" 
              style={fontStyles.subheading}>
              7. Vendor Management
            </h2>
            
            <p className="mb-6" style={fontStyles.body}>
              As part of your vendor ecosystem, we understand the importance of transparency in our own vendor relationships:
            </p>
            
            <ul className="mb-6 list-disc pl-6 space-y-4" style={fontStyles.body}>
              <li>
                <strong style={{fontWeight: 500}}>Subprocessors:</strong> We carefully select and vet our own vendors and subprocessors, ensuring they meet our security and compliance standards. A current list of subprocessors is available upon request.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Vendor Risk Assessment:</strong> We conduct regular security assessments of our vendors who may have access to customer data.
              </li>
              
              <li>
                <strong style={{fontWeight: 500}}>Enterprise Support:</strong> For enterprise customers, we can provide detailed information about our security practices to help you complete your vendor assessment processes.
              </li>
            </ul>
            
            {/* Concluding section */}
            <div className="border-t my-12 pt-12" style={{borderColor: colors.gray200}}>
              <h2 className="text-2xl mb-4" 
                style={fontStyles.heading}>
                Our Compliance Commitment
              </h2>
              <p className="mb-6" style={fontStyles.body}>
                SignPact is committed to maintaining and improving our compliance posture as we grow. We regularly review and update our security practices to align with evolving regulatory requirements and industry best practices.
              </p>
              
              <p className="mb-4" style={fontStyles.body}>
                For enterprise customers with specific compliance questions or requirements, our team is available to discuss your needs in detail. Please contact us to request our security documentation, arrange a compliance review, or discuss custom requirements.
              </p>
              
              <p className="mb-6" style={fontStyles.body}>
                <strong style={{fontWeight: 500}}>Note:</strong> As a growing company, our compliance journey is ongoing. We are transparent about our current status and roadmap for additional certifications and compliance measures. This page will be updated regularly as we achieve new compliance milestones.
              </p>
              
              <p style={fontStyles.body}>
                For compliance-related inquiries, please contact us at <span style={{color: colors.accent}}>founder@signpact.ai</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
      </motion.div>
      
      {/* Section Divider - Rail-style horizontal lines */}
      <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Gap between double rails */}
        <div 
          className="w-full"
          style={{
            height: '8px',
            background: 'transparent'
          }}
        />
        {/* Double rail effect - second line */}
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div>
      
      {/* Footer */}
      <ResponsiveFooter activePage="compliance" />
    </div>
  );
};

export default CompliancePage; 